import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { productsAPI, storesAPI, API_URL } from '../lib/api';
import { useCart } from '../context/CartContext';
import {
  Plus,
  Minus,
  Search,
  Filter,
  Grid,
  List,
  ShoppingCart,
  Check,
  Package,
  SortAsc,
  X,
  Edit2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Product {
  id: string;
  _id: string;
  name: string;
  description?: string;
  category: string;
  price: number;
  originalPrice?: number;
  discountPercentage?: number;
  unit: string;
  images: string[];
  stock: number;
  minStock: number;
  status: string;
  totalOrders: number;
  totalRevenue: number;
  isActive: boolean;
  isDealOfDay: boolean;
  dealEndTime?: string;
  tags: string[];
  storeId?: string | { _id: string; name: string };
  organizationId?: string;
}

const categories = [
  { id: 'all', name: 'All', icon: '🛒' },
  { id: 'Dairy', name: 'Dairy', icon: '🥛' },
  { id: 'Grains', name: 'Grains', icon: '🌾' },
  { id: 'Vegetables', name: 'Vegetables', icon: '🥬' },
  { id: 'Fruits', name: 'Fruits', icon: '🍎' },
  { id: 'Bakery', name: 'Bakery', icon: '🍞' },
];

const defaultImages: Record<string, string> = {
  Fruits: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400',
  Vegetables: 'https://images.unsplash.com/photo-1597362925123-77861d3fbac7?w=400',
  Dairy: 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400',
  Bakery: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400',
  Grains: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400',
  default: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400',
};

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { addToCart, removeFromCart, getCartItemQuantity, setIsCartOpen } = useCart();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [stores, setStores] = useState<any[]>([]);

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    discountPercentage: '',
    stock: '',
    minStock: '10',
    category: 'Dairy',
    unit: 'kg',
    images: [] as (File | string)[],
    tags: '',
    isDealOfDay: false,
    dealEndTime: '',
    storeId: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchProducts();
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await storesAPI.getAll();
      setStores(response.data || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const userData = localStorage.getItem('user');
      const currentUser = userData ? JSON.parse(userData) : null;

      const params: any = {};
      if (currentUser) {
        if (currentUser.storeId) params.storeId = currentUser.storeId;
        if (currentUser.organizationId) params.organizationId = currentUser.organizationId;
      }

      const response = await productsAPI.getAll(params);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', newProduct.name);
      formData.append('description', newProduct.description);
      formData.append('price', newProduct.price);
      if (newProduct.originalPrice) formData.append('originalPrice', newProduct.originalPrice);
      formData.append('stock', newProduct.stock);
      formData.append('minStock', newProduct.minStock);
      formData.append('category', newProduct.category);
      formData.append('unit', newProduct.unit);
      formData.append('tags', newProduct.tags);
      formData.append('isDealOfDay', String(newProduct.isDealOfDay));
      if (newProduct.dealEndTime) formData.append('dealEndTime', newProduct.dealEndTime);
      if (newProduct.storeId) formData.append('storeId', newProduct.storeId);

      if (newProduct.images && newProduct.images.length > 0) {
        newProduct.images.forEach((image) => {
          if (image instanceof File) {
            formData.append('images', image);
          } else if (typeof image === 'string') {
            formData.append('existingImages', image);
          }
        });
      }

      if (isEditMode && editingProductId) {
        await productsAPI.update(editingProductId, formData);
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(formData);
        toast.success('Product added successfully');
      }

      setIsAddModalOpen(false);
      setIsEditMode(false);
      setEditingProductId(null);
      fetchProducts();
      setNewProduct({
        name: '',
        description: '',
        price: '',
        originalPrice: '',
        discountPercentage: '',
        stock: '',
        minStock: '10',
        category: 'Dairy',
        unit: 'kg',
        images: [],
        tags: '',
        isDealOfDay: false,
        dealEndTime: '',
        storeId: '',
      });
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update product' : 'Failed to add product');
    }
  };

  const handleEditClick = (product: Product) => {
    setIsEditMode(true);
    setEditingProductId(product.id || product._id);
    setNewProduct({
      name: product.name,
      description: product.description || '',
      price: String(product.price),
      originalPrice: product.originalPrice ? String(product.originalPrice) : '',
      discountPercentage: product.discountPercentage ? String(product.discountPercentage) : '',
      stock: String(product.stock),
      minStock: String(product.minStock),
      category: product.category,
      unit: product.unit,
      images: product.images || [],
      tags: (product.tags || []).join(', '),
      isDealOfDay: product.isDealOfDay,
      dealEndTime: product.dealEndTime ? new Date(product.dealEndTime).toISOString().slice(0, 16) : '',
      storeId: typeof product.storeId === 'object' ? product.storeId._id : product.storeId || '',
    });
    setIsAddModalOpen(true);
  };

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, selectedCategory]);

  const filterProducts = () => {
    let filtered = products;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const getProductImage = (product: Product) => {
    if (product.images && product.images.length > 0 && product.images[0]) {
      const img = product.images[0];
      if (img.startsWith('/uploads')) {
        return `${API_URL}${img}`;
      }
      return img;
    }
    return defaultImages[product.category || ''] || defaultImages.default;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Browse Products</h1>
          <p className="text-gray-500 mt-1">{filteredProducts.length} products available in your area</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm font-semibold text-gray-700"
          >
            <ShoppingCart size={18} />
            View Cart
          </button>
          {['admin', 'super_user', 'entity_admin'].includes(user?.role) && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#10b981] text-white rounded-xl hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20 font-bold"
            >
              <Plus size={18} />
              Add Product
            </button>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay backdrop-blur-sm">
          <div className="modal-content max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                {isEditMode ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditMode(false);
                  setEditingProductId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddProduct} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Product Name *</label>
                    <input
                      type="text"
                      required
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Price (₹) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Original Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newProduct.originalPrice}
                        onChange={(e) => setNewProduct({ ...newProduct, originalPrice: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Stock *</label>
                      <input
                        type="number"
                        required
                        value={newProduct.stock}
                        onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Min Stock</label>
                      <input
                        type="number"
                        value={newProduct.minStock}
                        onChange={(e) => setNewProduct({ ...newProduct, minStock: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Category *</label>
                      <select
                        value={newProduct.category}
                        onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      >
                        {categories.filter(c => c.id !== 'all').map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Unit *</label>
                      <input
                        type="text"
                        required
                        placeholder="kg, pkt, ltr"
                        value={newProduct.unit}
                        onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Store</label>
                    <select
                      value={newProduct.storeId}
                      onChange={(e) => setNewProduct({ ...newProduct, storeId: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                    >
                      <option value="">Select Store</option>
                      {stores.map((store) => (
                        <option key={store._id} value={store._id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Product Images</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-200 border-dashed rounded-2xl hover:border-[#10b981] hover:bg-green-50/30 transition-all cursor-pointer relative group">
                      <div className="space-y-2 text-center">
                        <Plus className="mx-auto h-10 w-10 text-gray-300 group-hover:text-[#10b981] transition-colors" />
                        <div className="flex text-sm text-gray-600">
                          <label className="relative cursor-pointer rounded-md font-bold text-[#10b981] hover:text-[#059669]">
                            <span>Upload files</span>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              className="sr-only"
                              onChange={(e) => {
                                if (e.target.files) {
                                  const files = Array.from(e.target.files);
                                  setNewProduct({ ...newProduct, images: [...newProduct.images, ...files] });
                                }
                              }}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">PNG, JPG up to 10MB</p>
                      </div>
                    </div>
                    {newProduct.images.length > 0 && (
                      <div className="mt-4 grid grid-cols-4 gap-3">
                        {newProduct.images.map((img, index) => (
                          <div key={index} className="relative group aspect-square">
                            <img
                              src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                              alt="preview"
                              className="w-full h-full object-cover rounded-xl border border-gray-100"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newImages = newProduct.images.filter((_, i) => i !== index);
                                setNewProduct({ ...newProduct, images: newImages });
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Tags (comma separated)</label>
                    <input
                      type="text"
                      placeholder="fresh, organic, dairy"
                      value={newProduct.tags}
                      onChange={(e) => setNewProduct({ ...newProduct, tags: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="isDealOfDay"
                      checked={newProduct.isDealOfDay}
                      onChange={(e) => setNewProduct({ ...newProduct, isDealOfDay: e.target.checked })}
                      className="w-4 h-4 text-[#10b981] border-gray-300 rounded focus:ring-[#10b981]"
                    />
                    <label htmlFor="isDealOfDay" className="text-sm font-bold text-gray-700">Deal of the Day</label>
                  </div>
                  {newProduct.isDealOfDay && (
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Deal End Time</label>
                      <input
                        type="datetime-local"
                        value={newProduct.dealEndTime}
                        onChange={(e) => setNewProduct({ ...newProduct, dealEndTime: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none h-24 resize-none transition-all"
                />
              </div>
            </form>
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditMode(false);
                  setEditingProductId(null);
                }}
                className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddProduct}
                className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20"
              >
                {isEditMode ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl p-5 mb-8 border border-gray-100 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search products by name, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-[#10b981] focus:bg-white focus:border-transparent outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 transition-all font-semibold text-gray-700">
              <SortAsc size={18} />
              Name: A-Z
            </button>
            <button className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 transition-all font-semibold text-gray-700">
              <Filter size={18} />
              Filters
            </button>
            <div className="flex bg-gray-100 p-1 rounded-2xl">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-white text-[#10b981] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? 'bg-white text-[#10b981] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-3 mt-6 overflow-x-auto pb-2 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-2.5 px-5 py-2.5 rounded-xl whitespace-nowrap transition-all font-bold text-sm ${selectedCategory === cat.id
                ? 'bg-[#10b981] text-white shadow-lg shadow-[#10b981]/20 scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              <span className="text-lg">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      {
        filteredProducts.length > 0 ? (
          <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
            {filteredProducts.map((product) => {
              const productId = product.id || product._id;
              const quantity = getCartItemQuantity(productId);
              const isOutOfStock = product.stock === 0;
              const isLowStock = product.stock > 0 && product.stock < 10;

              return (
                <div
                  key={productId}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full"
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                      {isOutOfStock && (
                        <div className="px-3 py-1 bg-red-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                          Out of Stock
                        </div>
                      )}
                      {isLowStock && !isOutOfStock && (
                        <div className="px-3 py-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                          Low Stock
                        </div>
                      )}
                      {product.discountPercentage && (
                        <div className="px-3 py-1 bg-yellow-400 text-gray-900 text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                          {product.discountPercentage}% OFF
                        </div>
                      )}
                    </div>

                    {quantity > 0 && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#10b981] text-white text-[10px] font-bold uppercase tracking-widest rounded-full flex items-center gap-1 shadow-lg">
                        <Check size={12} />
                        {quantity} in cart
                      </div>
                    )}

                    {['admin', 'super_user', 'entity_admin'].includes(user?.role) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(product);
                        }}
                        className="absolute bottom-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-md text-gray-700 rounded-2xl flex items-center justify-center shadow-lg hover:bg-[#10b981] hover:text-white transition-all z-10 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0"
                        title="Edit Product"
                      >
                        <Edit2 size={18} />
                      </button>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#10b981] bg-green-50 px-2.5 py-1 rounded-lg">
                        {product.category}
                      </span>
                      {product.isDealOfDay && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-orange-600 bg-orange-50 px-2.5 py-1 rounded-lg">
                          Deal
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-[#10b981] transition-colors line-clamp-1">{product.name}</h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2 min-h-[2.5rem]">{product.description || 'No description available'}</p>

                    <div className="flex items-baseline gap-2 mb-5">
                      <span className="text-[#10b981] font-black text-2xl">₹{product.price.toFixed(2)}</span>
                      {product.originalPrice && product.originalPrice > product.price && (
                        <span className="text-gray-400 text-sm line-through font-medium">₹{product.originalPrice.toFixed(2)}</span>
                      )}
                      {product.unit && (
                        <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">/ {product.unit}</span>
                      )}
                    </div>

                    {/* Add to Cart */}
                    <div className="mt-auto">
                      {quantity > 0 ? (
                        <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-1.5 border border-gray-100">
                          <button
                            onClick={() => removeFromCart(productId)}
                            className="w-11 h-11 flex items-center justify-center bg-white rounded-xl text-gray-400 hover:text-red-500 hover:shadow-md transition-all border border-gray-100"
                          >
                            <Minus size={20} />
                          </button>
                          <span className="font-black text-gray-900 text-xl">{quantity}</span>
                          <button
                            onClick={() => addToCart(product)}
                            disabled={quantity >= product.stock}
                            className="w-11 h-11 flex items-center justify-center bg-[#10b981] rounded-xl text-white hover:bg-[#059669] hover:shadow-lg hover:shadow-[#10b981]/20 disabled:opacity-50 transition-all"
                          >
                            <Plus size={20} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addToCart(product)}
                          disabled={isOutOfStock}
                          className="w-full py-4 bg-[#10b981] text-white rounded-2xl font-bold hover:bg-[#059669] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#10b981]/10 hover:shadow-[#10b981]/20"
                        >
                          <ShoppingCart size={20} />
                          {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="text-gray-300" size={48} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500 max-w-xs mx-auto">Try adjusting your search or filters to find what you're looking for</p>
            <button
              onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
              className="mt-6 text-[#10b981] font-bold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )
      }
    </Layout>
  );
};

export default Products;